import { Button, ButtonLink, Dialog, Stack, toast, toastifyToast } from 'component-library';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { RestApiBadRequestServerError } from '@/api/rest/resources/errors/RestApiBadRequestError';
import { RestApiGeneralError } from '@/api/rest/resources/errors/RestApiGeneralError';
import { RestApiServerErrorStatusCode } from '@/api/rest/resources/errors/RestApiServerError';
import { Form } from '@/components';
import { parsePlotGroupBodyData } from '@/components/PlotGroups/utils/parsePlotGroupBodyData';
import { useAccountPageRoutes } from '@/hooks/useAccountPageRoutes';
import { useIsConservation } from '@/hooks/useIsConservation';
import { useScreenSize } from '@/hooks/useScreenSize';
import {
  EditLimitedAccessOrganizationFields,
  PlotGroups,
} from '@/pages/shared/components/plot-group/EditLimitedAccessOrganization';
import { useCreateLimitedAccessOrganization } from '@/pages/shared/hooks/useLimitedAccessOrganization';
import { useOrganizationId } from '@/pages/shared/hooks/useOrganizationId';
import { useOrganizationPlotGroups } from '@/pages/shared/hooks/useOrganizationPlotGroup';
import { useRestApiServerErrorHandling } from '@/utils/useRestApiServerErrorHandling';

import { Heading, SectionCard, SectionCardHeading } from '../../../../components';
import { InviteFormContent } from './components/FormContent';
import { SuccessDialogContent } from './components/SuccessDialogContent';
import { InviteInputs, useSendInvite } from './hooks/useSendInvite';
// NOTE: The form field names must match the api param names to make useRestApiServerErrorHandling work
const formFields = ['user.firstname', 'user.lastname', 'user.email', 'role'] as (keyof InviteInputs)[];

export const AccountTeamInvitePage = () => {
  const { t } = useTranslation();
  const isLargeScreen = useScreenSize() === 'large';
  const { accountTeamPath } = useAccountPageRoutes();

  const isConservation = useIsConservation();

  const organizationId = useOrganizationId();
  const plotGroupBodies = useOrganizationPlotGroups(organizationId).data;
  const plotGroups = plotGroupBodies.map((body) => parsePlotGroupBodyData(body));

  const inviteForm = useForm<InviteInputs>({
    defaultValues: {
      user: {
        firstname: '',
        lastname: '',
        email: '',
      },
      role: undefined,
    },
  });

  const laoForm = useForm<EditLimitedAccessOrganizationFields>({
    defaultValues: {
      plot_group_ids: [],
      user_ids: [],
    },
  });

  const handleServerError = useRestApiServerErrorHandling(inviteForm.setError, formFields);

  const { submit, isSubmitting } = useSendInvite();
  const { submit: submitLimitedAccessOrganization } = useCreateLimitedAccessOrganization();

  const [isDialogOpen, setIsDialogOpen] = useState(false);

  const sendInvite = async (inputs: InviteInputs) => {
    try {
      toastifyToast.clearWaitingQueue();
      toastifyToast.dismiss();

      const invitedUser = await submit(inputs);
      if (laoForm.getValues('plot_group_ids')?.length > 0) {
        submitLimitedAccessOrganization(organizationId, laoForm.getValues('plot_group_ids') ?? [], [invitedUser.id]);
      }

      setIsDialogOpen(true);
    } catch (error) {
      if (error instanceof RestApiBadRequestServerError) {
        handleServerError(error);
        return;
      }

      if (error instanceof RestApiGeneralError && error.statusCode === RestApiServerErrorStatusCode.TOO_MANY_REQUESTS) {
        toast({
          title: t('shared.account.teamInvite.errorToast.throttleTitle'),
          description: error.errors[0],
          type: 'error',
          autoClose: 10_000,
        });
        return;
      }

      toast({
        title: t('shared.account.teamInvite.errorToast.title'),
        description: t('shared.account.teamInvite.errorToast.description'),
        type: 'error',
        autoClose: 10_000,
      });
    }
  };

  const { user, role } = inviteForm.watch();

  const isSubmitButtonEnabled = !!user.firstname && !!user.lastname && !!user.email && !!role;

  return (
    <>
      <Form {...inviteForm}>
        <Heading backPath={accountTeamPath}>{t('shared.account.teamInvite.labels.title')}</Heading>
        <form onSubmit={inviteForm.handleSubmit(sendInvite)} className='flex flex-col gap-6 md:gap-9'>
          <fieldset disabled={isSubmitting}>
            <SectionCard>
              <SectionCardHeading>{t('shared.account.teamInvite.labels.inviteTeamMembers')}</SectionCardHeading>
              <Stack spacing={6}>
                <span className='typography-body1 max-w-lg to-text-primary'>{t('shared.account.teamInvite.copy')}</span>
                <Stack spacing={6} className='max-w-full sm:max-w-[220px]'>
                  <InviteFormContent form={inviteForm} />
                  {!isConservation && <PlotGroups form={laoForm} plotGroups={plotGroups} required={false} />}
                </Stack>
              </Stack>
            </SectionCard>
          </fieldset>
          <Stack direction='row' spacing={4} className='justify-end'>
            <ButtonLink to={accountTeamPath} variant='text' size={isLargeScreen ? 'medium' : 'small'}>
              {t('global.ui.buttons.cancel')}
            </ButtonLink>
            <Button
              type='submit'
              size={isLargeScreen ? 'medium' : 'small'}
              disabled={!isSubmitButtonEnabled}
              loading={isSubmitting}
            >
              {t('shared.account.teamInvite.labels.submitButton')}
            </Button>
          </Stack>
        </form>
      </Form>
      <Dialog
        open={isDialogOpen}
        onOpenChange={(isOpen: boolean) => {
          setIsDialogOpen(isOpen);
        }}
      >
        <SuccessDialogContent form={inviteForm} />
      </Dialog>
    </>
  );
};
